import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clearSession, getSession, loadCurrentUser, logoutCurrentSession, persistSession } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => getSession());
  const user = session?.user || null;
  const [isHydrating, setIsHydrating] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      try {
        const currentUser = await loadCurrentUser();
        if (cancelled) {
          return;
        }

        const nextSession = { user: currentUser };
        persistSession(nextSession);
        setSession(nextSession);
      } catch (_error) {
        if (cancelled) {
          return;
        }

        clearSession();
        setSession(null);
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
        }
      }
    }

    hydrateSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({
    user,
    role: user?.role || null,
    isAuthenticated: Boolean(user),
    isHydrating,
    hasRole: (...roles) => Boolean(user?.role && roles.includes(user.role)),
    login: (nextSession) => {
      const normalizedSession = { user: nextSession?.user || null };
      persistSession(normalizedSession);
      setSession(normalizedSession);
    },
    updateUser: (updatedUser) => {
      const nextSession = { ...session, user: { ...user, ...updatedUser } };
      persistSession(nextSession);
      setSession(nextSession);
    },
    logout: async () => {
      try {
        await logoutCurrentSession();
      } catch (_error) {
        // ignore remote logout failures and clear client state anyway
      } finally {
        clearSession();
        setSession(null);
      }
    },
  }), [user, session, isHydrating]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
